import {
  createPlaylistEntry,
  deduplicateEntries,
  extractYouTubeVideoId,
  getCompatibilityNotices,
  inferTitleFromUrl,
  mergeDocuments,
  parsePlaylist,
  serializePlaylist,
  validatePlaylist,
  type PlaylistDocument,
  type PlaylistEntry,
  type PlaylistFormat,
  type ProTVTarget,
} from "@protv-converter/core";
import { BookOpen, Code2, KeyRound } from "lucide-react";
import { useMemo, useState } from "react";
import { ExportPanel } from "./components/ExportPanel";
import { PlaylistEditor } from "./components/PlaylistEditor";
import { SettingsDialog } from "./components/SettingsDialog";
import { SourcePanel } from "./components/SourcePanel";
import { downloadTextFile, downloadThumbnailBundle } from "./lib/download";
import { readApiKey, writeApiKey } from "./lib/storage";
import { loadYouTubePlaylist, validateYouTubeApiKey, YouTubeApiError } from "./lib/youtube";

type MobilePane = "source" | "playlist" | "export";
type Notice = { tone: "success" | "warning" | "error"; message: string } | null;

const emptyDocument: PlaylistDocument = { header: "", entries: [] };

function parseManualLinks(value: string): PlaylistEntry[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line, index) => {
      const separator = line.lastIndexOf("|");
      const candidateUrl = separator >= 0 ? line.slice(separator + 1).trim() : line;
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(candidateUrl);
      } catch {
        return [];
      }
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") return [];

      const title = separator >= 0 ? line.slice(0, separator).trim() : inferTitleFromUrl(candidateUrl, index);
      const youtubeVideoId = extractYouTubeVideoId(candidateUrl) ?? undefined;
      return [
        createPlaylistEntry({
          mainUrl: candidateUrl,
          title,
          source: "manual",
          youtubeVideoId,
          thumbnailUrl: youtubeVideoId ? `https://i.ytimg.com/vi/${youtubeVideoId}/hqdefault.jpg` : undefined,
        }),
      ];
    });
}

function noticeClass(tone: NonNullable<Notice>["tone"]): string {
  if (tone === "success") return "border-valid/35 bg-valid-soft text-valid";
  if (tone === "warning") return "border-warning/35 bg-warning-soft text-warning";
  return "border-danger/35 bg-danger-soft text-danger";
}

export default function App() {
  const [document, setDocument] = useState<PlaylistDocument>(emptyDocument);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [target, setTarget] = useState<ProTVTarget>("3.x");
  const [format, setFormat] = useState<PlaylistFormat>("tokenized");
  const [filename, setFilename] = useState("ProTV-Playlist");
  const [urlPrefix, setUrlPrefix] = useState("");
  const [bundleThumbnails, setBundleThumbnails] = useState(false);
  const [apiKey, setApiKey] = useState(readApiKey);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");
  const [notice, setNotice] = useState<Notice>(null);
  const [mobilePane, setMobilePane] = useState<MobilePane>("source");

  const issues = useMemo(() => validatePlaylist(document), [document]);
  const compatibilityNotices = useMemo(
    () => getCompatibilityNotices(document, target, format),
    [document, target, format],
  );
  const preview = useMemo(
    () => (document.entries.length > 0 ? serializePlaylist(document, { target, format, urlPrefix }) : ""),
    [document, target, format, urlPrefix],
  );

  const placeEntries = (incoming: PlaylistDocument, append: boolean) => {
    const next = append ? mergeDocuments(document, incoming) : incoming;
    setDocument(next);
    setSelectedId(next.entries[0]?.id ?? null);
    setMobilePane("playlist");
  };

  const handleLoadYouTube = async (value: string, append: boolean) => {
    if (!apiKey) {
      setNotice({ tone: "warning", message: "Add a YouTube Data API key before loading a YouTube playlist." });
      setSettingsOpen(true);
      return;
    }

    setIsLoading(true);
    setLoadedCount(0);
    setNotice(null);
    try {
      const entries = await loadYouTubePlaylist(value, apiKey, setLoadedCount);
      if (entries.length === 0) throw new YouTubeApiError("not-found", "No available videos were returned for that playlist.");
      const merged = append ? mergeDocuments(document, { header: "", entries }) : { header: "", entries };
      const result = deduplicateEntries(merged);
      setDocument(result.document);
      setSelectedId(result.document.entries[0]?.id ?? null);
      setMobilePane("playlist");
      setNotice({
        tone: "success",
        message: `Loaded ${entries.length} available ${entries.length === 1 ? "video" : "videos"}${result.removed > 0 ? ` and skipped ${result.removed} duplicate ${result.removed === 1 ? "URL" : "URLs"}` : ""}.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "The YouTube playlist could not be loaded.";
      setNotice({ tone: "error", message });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasteLinks = (value: string, append: boolean) => {
    const entries = parseManualLinks(value);
    if (entries.length === 0) {
      setNotice({ tone: "error", message: "No valid HTTP or HTTPS media links were found." });
      return;
    }
    const merged = append ? mergeDocuments(document, { header: "", entries }) : { header: "", entries };
    const result = deduplicateEntries(merged);
    setDocument(result.document);
    setSelectedId(result.document.entries[0]?.id ?? null);
    setMobilePane("playlist");
    setNotice({ tone: "success", message: `Added ${entries.length} ${entries.length === 1 ? "link" : "links"}${result.removed > 0 ? `; ${result.removed} duplicate skipped` : ""}.` });
  };

  const handleImportFile = async (file: File, append: boolean) => {
    try {
      const result = parsePlaylist(await file.text());
      placeEntries(result.document, append);
      setTarget(result.detectedTarget);
      setFormat(result.format);
      setFilename(file.name.replace(/\.(playlist|txt|json)$/i, "") || "ProTV-Playlist");
      const additional = result.notices[0]?.message ? ` ${result.notices[0].message}` : "";
      setNotice({ tone: result.notices.some((item) => item.level === "warning") ? "warning" : "success", message: `Imported ${result.document.entries.length} entries as ProTV ${result.detectedTarget}.${additional}` });
    } catch (error) {
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "The playlist file could not be read." });
    }
  };

  const handleUpdate = (entryId: string, patch: Partial<PlaylistEntry>) => {
    setDocument((current) => ({ ...current, entries: current.entries.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)) }));
  };

  const handleRemove = (entryId: string) => {
    setDocument((current) => ({ ...current, entries: current.entries.filter((entry) => entry.id !== entryId) }));
    if (selectedId === entryId) setSelectedId(null);
  };

  const handleMove = (entryId: string, direction: -1 | 1) => {
    setDocument((current) => {
      const index = current.entries.findIndex((entry) => entry.id === entryId);
      const destination = index + direction;
      if (index < 0 || destination < 0 || destination >= current.entries.length) return current;
      const entries = [...current.entries];
      const [entry] = entries.splice(index, 1);
      if (!entry) return current;
      entries.splice(destination, 0, entry);
      return { ...current, entries };
    });
  };

  const handleClear = () => {
    if (!window.confirm("Clear every playlist entry?")) return;
    setDocument(emptyDocument);
    setSelectedId(null);
    setNotice({ tone: "success", message: "The current playlist was cleared." });
  };

  const handleDeduplicate = () => {
    const result = deduplicateEntries(document);
    setDocument(result.document);
    setNotice({ tone: result.removed > 0 ? "success" : "warning", message: result.removed > 0 ? `Removed ${result.removed} duplicate ${result.removed === 1 ? "entry" : "entries"}.` : "No duplicate main URLs were found." });
  };

  const handleCopy = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview);
      setNotice({ tone: "success", message: "The current export was copied to the clipboard." });
    } catch {
      setNotice({ tone: "error", message: "The browser blocked clipboard access. Use the download instead." });
    }
  };

  const handleExport = async () => {
    const blocking = issues.filter((issue) => issue.level === "error");
    if (blocking.length > 0) {
      setNotice({ tone: "error", message: `Fix ${blocking.length} blocking ${blocking.length === 1 ? "entry issue" : "entry issues"} before exporting.` });
      setMobilePane("playlist");
      return;
    }

    setIsExporting(true);
    setExportProgress("");
    try {
      const options = { target, format, urlPrefix, includeImagePaths: true } as const;
      if (bundleThumbnails) {
        const result = await downloadThumbnailBundle(document, filename, options, (completed, total) => setExportProgress(`Downloading thumbnail ${completed} of ${total}`));
        setNotice({ tone: result.failed > 0 ? "warning" : "success", message: `Downloaded the playlist bundle with ${result.downloaded} ${result.downloaded === 1 ? "thumbnail" : "thumbnails"}${result.failed > 0 ? `; ${result.failed} could not be fetched` : ""}.` });
      } else {
        downloadTextFile(preview, filename);
        setNotice({ tone: "success", message: "Downloaded the version-correct ProTV playlist." });
      }
    } catch (error) {
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "The export could not be prepared." });
    } finally {
      setIsExporting(false);
      setExportProgress("");
    }
  };

  const handleValidateAndSave = async (value: string) => {
    try {
      await validateYouTubeApiKey(value);
      writeApiKey(value);
      setApiKey(value);
      setSettingsOpen(false);
      setNotice({ tone: "success", message: "The YouTube API key was validated and saved locally." });
    } catch (error) {
      setNotice({ tone: "error", message: error instanceof Error ? error.message : "The YouTube API key could not be validated." });
      throw error;
    }
  };

  const handleClearApiKey = () => {
    writeApiKey("");
    setApiKey("");
    setNotice({ tone: "success", message: "The saved YouTube API key was removed from this browser." });
  };

  const sourcePaneClass = mobilePane === "source" ? "block" : "hidden lg:block";
  const playlistPaneClass = mobilePane === "playlist" ? "block" : "hidden lg:block";
  const exportPaneClass = mobilePane === "export" ? "block" : "hidden lg:block";

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <header className="sticky top-0 z-40 flex min-h-14 items-center gap-3 border-b border-white/10 bg-shell px-3 text-shell-text sm:px-5">
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-semibold tracking-tight sm:text-2xl">ProTV Converter</h1>
        </div>
        <p className="hidden border-l border-white/15 pl-3 text-xs text-white/65 md:block">Build the playlist. Pick the target. Export.</p>
        <nav className="ml-auto flex items-center gap-1" aria-label="Product links">
          <a aria-label="Open ProTV playlist documentation" className="header-button" href="https://protv.dev/guides/playlist" target="_blank" rel="noreferrer"><BookOpen aria-hidden="true" size={16} /><span className="hidden sm:inline">ProTV docs</span></a>
          <a aria-label="Open the ProTV Converter source repository" className="header-button" href="https://github.com/ifBars/VRChat-ProTVConverter" target="_blank" rel="noreferrer"><Code2 aria-hidden="true" size={16} /><span className="hidden sm:inline">Source</span></a>
          <button aria-label="Open API key settings" aria-pressed={settingsOpen} className={`header-button ${apiKey ? "text-white" : "text-warning-light"}`} type="button" onClick={() => setSettingsOpen(true)}><KeyRound aria-hidden="true" size={16} /><span className="hidden sm:inline">{apiKey ? "API key saved" : "Add API key"}</span></button>
        </nav>
      </header>

      <nav className="grid grid-cols-3 border-b border-line bg-paper lg:hidden" aria-label="Mobile workflow">
        {(["source", "playlist", "export"] as const).map((pane) => (
          <button className={`min-h-11 cursor-pointer border-b-2 text-sm font-semibold capitalize ${mobilePane === pane ? "border-accent text-accent" : "border-transparent text-muted"}`} type="button" aria-current={mobilePane === pane ? "step" : undefined} key={pane} onClick={() => setMobilePane(pane)}>{pane}{pane === "playlist" && document.entries.length > 0 ? ` (${document.entries.length})` : ""}</button>
        ))}
      </nav>

      {notice ? (
        <div className={`flex min-h-10 items-center justify-between gap-3 border-b px-4 py-2 text-sm ${noticeClass(notice.tone)}`} role={notice.tone === "error" ? "alert" : "status"}>
          <p>{notice.message}</p>
          <button className="cursor-pointer text-xs font-semibold underline underline-offset-4" type="button" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      ) : null}

      <main className="grid min-h-[calc(100dvh-6.5rem)] lg:grid-cols-[19rem_minmax(0,1fr)_21rem]">
        <div className={sourcePaneClass}>
          <SourcePanel apiKeyConfigured={Boolean(apiKey)} isLoading={isLoading} loadedCount={loadedCount} onOpenSettings={() => setSettingsOpen(true)} onLoadYouTube={handleLoadYouTube} onPasteLinks={handlePasteLinks} onImportFile={handleImportFile} />
        </div>
        <div className={playlistPaneClass}>
          <PlaylistEditor document={document} selectedId={selectedId} issues={issues} onHeaderChange={(header) => setDocument((current) => ({ ...current, header }))} onSelect={setSelectedId} onUpdate={handleUpdate} onRemove={handleRemove} onMove={handleMove} onClear={handleClear} onDeduplicate={handleDeduplicate} onCopyPreview={() => void handleCopy()} />
        </div>
        <div className={exportPaneClass}>
          <ExportPanel document={document} target={target} format={format} filename={filename} urlPrefix={urlPrefix} bundleThumbnails={bundleThumbnails} notices={compatibilityNotices} preview={preview} isExporting={isExporting} exportProgress={exportProgress} onTargetChange={(value) => { setTarget(value); if (value !== "3.x") setFormat("tokenized"); }} onFormatChange={setFormat} onFilenameChange={setFilename} onUrlPrefixChange={setUrlPrefix} onBundleChange={setBundleThumbnails} onCopy={() => void handleCopy()} onExport={handleExport} />
        </div>
      </main>

      {settingsOpen ? <SettingsDialog currentKey={apiKey} onClose={() => setSettingsOpen(false)} onValidateAndSave={handleValidateAndSave} onClear={handleClearApiKey} /> : null}
    </div>
  );
}
